import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { subscriptionAPI } from '../services/api';
import { ga } from '../services/ga';
import { useAuth } from './AuthContext';
import { isIOS } from '../services/native';

const SubscriptionContext = createContext(null);

// Detect currency from user's phone number or timezone
// +91 or Indian numbers → INR, everything else → USD
const detectCurrency = (phone, timezone) => {
  // Try phone number first (most reliable for registered users)
  if (phone) {
    const cleaned = phone.replace(/[\s\-()]/g, '');
    // Indian numbers: +91, 91 with 12+ digits, or 10-digit starting with 6-9
    if (cleaned.startsWith('+91') || (cleaned.startsWith('91') && cleaned.length >= 12)) return 'INR';
    if (/^[6-9]\d{9}$/.test(cleaned)) return 'INR';
    // Any other country code → USD
    if (cleaned.startsWith('+')) return 'USD';
  }
  // Fallback: check user's timezone (for Google/Apple sign-in users without phone)
  if (timezone) {
    if (timezone.startsWith('Asia/Kolkata') || timezone.startsWith('Asia/Calcutta')) return 'INR';
    return 'USD';
  }
  // Last resort: check browser timezone
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (tz.startsWith('Asia/Kolkata') || tz.startsWith('Asia/Calcutta')) return 'INR';
    if (tz.startsWith('Asia/')) return 'INR'; // Broad India fallback
  } catch {}
  return 'INR';
};

// Detect iOS source: URL param, localStorage, or iOS browser user agent
const getIOSSource = () => {
  try {
    // Check URL param first (from marketing site CTA links)
    const params = new URLSearchParams(window.location.search);
    if (params.get('source') === 'ios') {
      localStorage.setItem('hj_platform', 'ios');
      return 'ios';
    }
    // Check persisted value
    if (localStorage.getItem('hj_platform') === 'ios') {
      return 'ios';
    }
    // Detect iOS browser (Safari on iPhone/iPad) — show iOS pricing for web users too
    const ua = navigator.userAgent || '';
    if (/iPhone|iPad|iPod/i.test(ua)) {
      localStorage.setItem('hj_platform', 'ios');
      return 'ios';
    }
    return null;
  } catch {
    return null;
  }
};

export function SubscriptionProvider({ children }) {
  const { isAuthenticated, user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [freeAccessMode, setFreeAccessMode] = useState(false);
  const [iosPaymentHidden, setIosPaymentHidden] = useState(false);
  const [referralEnabled, setReferralEnabled] = useState(false);
  const [platformSource] = useState(() => getIOSSource());
  const [userCurrency, setUserCurrency] = useState('INR');

  // Detect currency from user's phone number or timezone
  useEffect(() => {
    if (user) {
      setUserCurrency(detectCurrency(user.phone, user.config?.timezone));
    }
  }, [user?.phone, user?.config?.timezone]);

  // Fetch subscription status when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchSubscription();
    } else {
      setSubscription(null);
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Fetch plans on mount (public endpoint)
  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchSubscription = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await subscriptionAPI.getSubscription();
      // API returns { subscription: {...}, status, isPremium, freeAccessMode, ... }
      // We store the subscription object directly for easier access
      setSubscription(data.data.subscription || data.data);
      if (data.data.freeAccessMode !== undefined) {
        setFreeAccessMode(data.data.freeAccessMode);
      }
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
      setError(err.error || 'Failed to fetch subscription');
      // Don't set subscription to null on error - keep previous state
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPlans = useCallback(async () => {
    try {
      const { data } = await subscriptionAPI.getPlans(platformSource);
      setPlans(data.data);
      if (data.data.freeAccessMode !== undefined) {
        setFreeAccessMode(data.data.freeAccessMode);
      }
      if (data.data.iosPaymentHidden !== undefined) {
        setIosPaymentHidden(data.data.iosPaymentHidden);
      }
      if (data.data.referralEnabled !== undefined) {
        setReferralEnabled(data.data.referralEnabled);
      }
    } catch (err) {
      console.error('Failed to fetch plans:', err);
    }
  }, [platformSource]);

  const startTrial = useCallback(async () => {
    try {
      setError(null);
      const { data } = await subscriptionAPI.startTrial();
      // API returns { subscription: {...}, message }
      setSubscription(data.data.subscription || data.data);
      return data.data;
    } catch (err) {
      const errorMessage = err.error || 'Failed to start trial';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  const applyCoupon = useCallback(async (code, billingInterval) => {
    try {
      setError(null);
      const { data } = await subscriptionAPI.applyCoupon(code, userCurrency, billingInterval);
      setAppliedCoupon({
        ...data.data.coupon,
        discountAmount: data.data.pricing.discountAmount,
        finalAmount: data.data.pricing.finalAmount,
        formattedDiscount: data.data.pricing.formattedDiscount,
        formattedFinal: data.data.pricing.formattedFinal
      });
      setCouponCode(code);
      return data.data;
    } catch (err) {
      const errorMessage = err.error || 'Invalid coupon code';
      setError(errorMessage);
      setAppliedCoupon(null);
      throw new Error(errorMessage);
    }
  }, [userCurrency]);

  const removeCoupon = useCallback(() => {
    setAppliedCoupon(null);
    setCouponCode('');
    setError(null);
  }, []);

  const createCheckout = useCallback(async (couponCodeToApply = null, billingInterval = 'monthly') => {
    try {
      setError(null);
      const { data } = await subscriptionAPI.checkout(
        couponCodeToApply || couponCode || undefined,
        billingInterval,
        platformSource,
        userCurrency
      );
      // Funnel step: user started checkout (before payment completes).
      ga.beginCheckout({ currency: userCurrency, interval: billingInterval });
      return data.data;
    } catch (err) {
      const errorMessage = err.error || 'Failed to create checkout';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [couponCode, platformSource, userCurrency]);

  const verifyPayment = useCallback(async (paymentData) => {
    try {
      setError(null);
      const { data } = await subscriptionAPI.verifyPayment(paymentData);
      const sub = data.data.subscription;
      setSubscription(sub);
      ga.purchase({
        amount: sub?.amount,
        currency: sub?.currency || 'INR',
        interval: sub?.billingInterval,
      });
      // Clear coupon after successful payment
      setAppliedCoupon(null);
      setCouponCode('');
      return data.data;
    } catch (err) {
      const errorMessage = err.error || 'Payment verification failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  const switchPlan = useCallback(async (billingInterval) => {
    try {
      setError(null);
      const { data } = await subscriptionAPI.switchPlan(billingInterval);
      setSubscription(prev => ({ ...prev, ...data.data.subscription }));
      return data.data;
    } catch (err) {
      const errorMessage = err.error || 'Failed to switch plan';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  const cancelSubscription = useCallback(async (reason = '') => {
    try {
      setError(null);
      const { data } = await subscriptionAPI.cancel(reason);
      // Merge with existing subscription data to preserve fields like trialEndDate
      setSubscription(prev => ({ ...prev, ...data.data.subscription }));
      return data.data;
    } catch (err) {
      const errorMessage = err.error || 'Failed to cancel subscription';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  const pauseSubscription = useCallback(async () => {
    try {
      setError(null);
      const { data } = await subscriptionAPI.pause();
      // Merge with existing subscription data
      setSubscription(prev => ({ ...prev, ...data.data.subscription }));
      return data.data;
    } catch (err) {
      const errorMessage = err.error || 'Failed to pause subscription';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  const resumeSubscription = useCallback(async () => {
    try {
      setError(null);
      const { data } = await subscriptionAPI.resume();
      // Merge with existing subscription data
      setSubscription(prev => ({ ...prev, ...data.data.subscription }));
      return data.data;
    } catch (err) {
      const errorMessage = err.error || 'Failed to resume subscription';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  const getInvoices = useCallback(async (params = {}) => {
    try {
      const { data } = await subscriptionAPI.getInvoices(params);
      return data.data;
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
      throw err;
    }
  }, []);

  // Computed values
  const isPremium = useMemo(() => {
    if (freeAccessMode) return true;
    if (['trial', 'active'].includes(subscription?.status)) return true;
    // Paused/cancelled users still have access until their period ends
    if (['paused', 'cancelled'].includes(subscription?.status) && subscription?.currentPeriodEnd) {
      return new Date(subscription.currentPeriodEnd) > new Date();
    }
    return false;
  }, [freeAccessMode, subscription?.status, subscription?.currentPeriodEnd]);

  const isTrialing = useMemo(() => {
    return subscription?.status === 'trial';
  }, [subscription?.status]);

  const isActive = useMemo(() => {
    return subscription?.status === 'active';
  }, [subscription?.status]);

  const isPaused = useMemo(() => {
    return subscription?.status === 'paused';
  }, [subscription?.status]);

  const isCancelled = useMemo(() => {
    return subscription?.status === 'cancelled';
  }, [subscription?.status]);

  const isExpired = useMemo(() => {
    return subscription?.status === 'expired';
  }, [subscription?.status]);

  const trialDaysLeft = useMemo(() => {
    if (!subscription?.trialEndDate) return 0;
    const now = new Date();
    const trialEnd = new Date(subscription.trialEndDate);
    const diff = trialEnd - now;
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }, [subscription?.trialEndDate]);

  const subscriptionDaysLeft = useMemo(() => {
    if (!subscription?.currentPeriodEnd) return 0;
    const now = new Date();
    const periodEnd = new Date(subscription.currentPeriodEnd);
    const diff = periodEnd - now;
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }, [subscription?.currentPeriodEnd]);

  // Hide payment UI on iOS when admin flag is enabled
  const hidePaymentUI = useMemo(() => {
    return isIOS && iosPaymentHidden;
  }, [iosPaymentHidden]);

  // Will the subscription cancel at period end?
  const willCancel = useMemo(() => {
    return !!subscription?.willCancelAt;
  }, [subscription?.willCancelAt]);

  // Get the formatted price with discount
  const getFinalPrice = useMemo(() => {
    const cur = userCurrency || 'INR';
    if (!plans?.pricing?.[cur]) return null;
    const originalAmount = plans.pricing[cur].amount;
    const symbol = cur === 'INR' ? '₹' : '$';
    const decimals = cur === 'INR' ? 0 : 2;

    if (!appliedCoupon) {
      return {
        original: originalAmount,
        final: originalAmount,
        discount: 0,
        formatted: plans.pricing[cur].formatted,
        currency: cur
      };
    }

    const discountAmount = appliedCoupon.discountAmount || 0;
    const finalAmount = originalAmount - discountAmount;

    return {
      original: originalAmount,
      final: finalAmount,
      discount: discountAmount,
      formatted: `${symbol}${(finalAmount / 100).toFixed(decimals)}`,
      currency: cur
    };
  }, [plans, appliedCoupon, userCurrency]);

  const value = {
    // State
    subscription,
    plans,
    loading,
    error,
    couponCode,
    appliedCoupon,
    freeAccessMode,
    iosPaymentHidden,
    hidePaymentUI,
    referralEnabled,
    platformSource,
    userCurrency,

    // Computed
    isPremium,
    isTrialing,
    isActive,
    isPaused,
    isCancelled,
    isExpired,
    trialDaysLeft,
    subscriptionDaysLeft,
    willCancel,
    getFinalPrice,

    // Actions
    refreshSubscription: fetchSubscription,
    fetchPlans,
    startTrial,
    applyCoupon,
    removeCoupon,
    setCouponCode,
    createCheckout,
    verifyPayment,
    switchPlan,
    cancelSubscription,
    pauseSubscription,
    resumeSubscription,
    getInvoices,
    clearError: () => setError(null)
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}
